// src/modules/app-views/crm/pages/Kanban/modals/EditOpportunityFormModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { ConfigProvider } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import useZustand from 'hooks/useZustand';
import { darkTheme, lightTheme } from 'configs/appEngine';
import axiosService from 'services/axiosService';
import appService from 'services/appService';
import translateX from 'utils/translateX';
import { API } from 'constants/Api';
import Step1 from './Step1';

// *******************************************************************************
// THEME WRAPPER
// *******************************************************************************
const ThemeWrapper = ({ children }) => {
  const theme = useZustand('theme', 'currentTheme');
  const config = theme === 'dark' ? darkTheme : lightTheme;
  return <ConfigProvider theme={config}>{children}</ConfigProvider>;
};

// *******************************************************************************
// MAIN MODAL COMPONENT
// *******************************************************************************
const EditOpportunityFormModal = ({ show, setShow, afterClose, initialValues, crmData, card, onSuccess }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedParameters = useZustand('app', 'parameters');
  const crmDataRef = useRef(crmData);

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const boards = storedParameters?.modules?.crm?.boards || [];
  const selectedBoard = boards.find(b => b.uid === crmData?.boardUid);
  const boardFormFields = selectedBoard?.formFields || [];
  const opportunityFieldKeys = selectedBoard?.opportunityField || [];
  const intrinsicFieldLabels = selectedBoard?.intrinsicFieldLabels || {};
  const columnUid = crmData?.columnUid || null;
  const hasOpportunityComposition = opportunityFieldKeys.length > 0;

  // Build validationFields
  const sortedColumns = [...(selectedBoard?.columns || [])].sort(
    (a, b) => (a.order || 0) - (b.order || 0)
  );
  const initialColumn = sortedColumns.find(c => c.initial) || sortedColumns[0] || null;
  const initialColumnUid = initialColumn?.uid || columnUid || null;

  const requiredFormFields = boardFormFields
    .filter(f => (f.requiredInColumns || []).includes(columnUid))
    .map(f => ['formData', f.fieldName]);

  const validationFields = [
    'name',
    ['phone', 'value'],
    ['email', 'value'],
    'consultantId',
    ...(hasOpportunityComposition ? [] : ['opportunity']),
    ...requiredFormFields,
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ form }) => {
    const values = form.getFieldsValue(true);
    const data = crmDataRef.current;

    // Phone OR email — at least one required
    const phone = values?.phone?.value?.trim() || '';
    const email = values?.email?.value?.trim() || '';
    if (!phone && !email) {
      appService.message({ flag: 'w', text: 'phone_or_email_required' });
      return false;
    }

    // Compose opportunity from custom fields or use manual input
    const opportunity = hasOpportunityComposition
      ? composeOpportunity(values.formData, opportunityFieldKeys)
      : (values.opportunity || '').trim();

    const consultantId = values.consultantId || null;

    // 1. Update CRM record
    await axiosService.put({
      url: API.CRM.URL.replace(':_id?', card._id),
      data: {
        opportunity: opportunity || 'n/a',
        uniqueKey: (values.uniqueKey || '').trim() || null,
        consultantId,
        ...(values.formData ? { formData: values.formData } : {}),
      },
      returnAsObject: true,
    });

    // 2. Update person entity
    const personEntityId = data?.personContactId || data?.entityId;
    if (personEntityId) {
      const personPayload = { name: (values.name || '').trim() };

      if (values.phone?.value?.trim()) {
        personPayload.phone = {
          value: values.phone.value.trim(),
          countryCode: values.phone.countryCode || 'BR',
          type: values.phone.type || 'mobile',
        };
      }

      if (values.email?.value?.trim()) {
        personPayload.email = {
          value: values.email.value.trim(),
          countryCode: 'BR',
        };
      }

      await axiosService.put({
        url: API.ENTITIES.URL.replace(':_id?', personEntityId),
        data: personPayload,
        returnAsObject: true,
      });
    }

    window.dispatchEvent(new CustomEvent('crm:kanban:refresh'));
    appService.message({ flag: 's', text: 'saved_successfully' });
    onSuccess?.();
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const steps = useMemo(
    () => [
      {
        stepTitle: 'edit_opportunity',
        validationFields,
        content: () => (
          <Step1
            formFields={boardFormFields}
            columnUid={columnUid}
            opportunityField={opportunityFieldKeys}
            intrinsicFieldLabels={intrinsicFieldLabels}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [boardFormFields, columnUid, opportunityFieldKeys]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      afterClose={afterClose}
      title={translateX('edit_opportunity')}
      icon={<EditOutlined />}
      steps={steps}
      onFinish={handleFinish}
      initialValues={initialValues}
      width={540}
    />
  );
};

// *******************************************************************************
// IMPERATIVE SHOW FUNCTION (default export)
// *******************************************************************************
function showEditOpportunityFormModal({ card, onSuccess } = {}) {
  axiosService
    .get({ url: API.CRM.URL.replace(':_id?', card._id), returnAsObject: true })
    .then(([crmData]) => {
      if (!crmData) return;

      const personName = card.__personContact?.name || card.__personContact?.firstName
        || card.__entity?.name || card.__entity?.firstName || '';

      const phoneValue = card.__personContact?.phone?.value || card.__entity?.phone?.value;
      const emailValue = card.__personContact?.email?.value || card.__entity?.email?.value;

      const initialValues = {
        name: personName,
        phone: phoneValue ? { value: phoneValue } : undefined,
        email: emailValue ? { value: emailValue } : undefined,
        uniqueKey: crmData.uniqueKey || '',
        opportunity: crmData.opportunity || '',
        consultantId: crmData.consultantId || undefined,
        formData: crmData.formData || {},
      };

      const container = document.createElement('div');
      container.id = 'edit-opportunity-form-modal-container';
      document.body.appendChild(container);

      const root = createRoot(container);
      let isModalOpen = true;

      const renderModal = () => {
        root.render(
          <ThemeWrapper>
            <EditOpportunityFormModal
              show={isModalOpen}
              setShow={handleClose}
              afterClose={handleAfterClose}
              initialValues={initialValues}
              crmData={crmData}
              card={card}
              onSuccess={onSuccess}
            />
          </ThemeWrapper>
        );
      };

      const handleClose = () => {
        isModalOpen = false;
        renderModal();
      };

      const handleAfterClose = () => {
        setTimeout(() => {
          root.unmount();
          if (document.body.contains(container)) {
            document.body.removeChild(container);
          }
        }, 0);
      };

      renderModal();
    });
}

export default showEditOpportunityFormModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function composeOpportunity(formData, fieldKeys) {
  if (!formData || !fieldKeys?.length) return '';
  return fieldKeys
    .map(key => {
      const val = formData[key];
      if (val == null) return '';
      return String(val).trim();
    })
    .filter(Boolean)
    .join(' ');
}
